import React, { useEffect, useState } from 'react';
import styles from './_customer-portal-page.module.scss';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { AppState } from '../../../Store/rootReducer';
import { failedLogin, getUser, portalValidationError, scheduleAppointment, setUser, updatePrefersSpanish } from '../../../Store/CustomerPortal/actions';
import useWindowDimensions from '../../../Hooks/useWindowDimensions';
import { Dialog } from '@progress/kendo-react-dialogs';
import {
  Stepper,
  StepperChangeEvent,
  StepProps,
} from '@progress/kendo-react-layout';
import { Loader } from "@progress/kendo-react-indicators";
import { checkIcon, linkVerticalIcon } from '@progress/kendo-svg-icons';
import { getCustomerRoleName, getWordingForLanguage } from '../../../Helpers/LanguageDictionary';
import { CustomerPortalLogin } from '../../../Models/CustomerPortalLogin';
import SSNInputGroup from '../../Controls/SSNInputGroup/SSNInputGroup';
import { Button } from '@progress/kendo-react-buttons';
import NameDoBLogin from '../../Controls/NameDateOfBirthLogin/NameDateOfBirthLogin';
import { isNil, isNotNil } from 'ramda';
import { httpGetAsync, httpPostAsync } from '../../../Http/HttpClient';
import { CustomerPortalUser } from '../../../Models/CustomerPortalUser';
import { capitalizeFirstLetterOfEachWord, displayError, sortCustomers, stringToBoolean } from '../../../Helpers/Utilities';
import { PortalStipulations } from '../../PortalStipulations/PortalStipulations';
import { PortalReferences } from '../../PortalReferences/PortalReferences';
import { setClient, setCustomerPortalLinkId, setSelectedLanguageId } from '../../../Store/Shared/actions';
import { SvgIcon } from '@progress/kendo-react-common';
import { PortalAppointment } from '../../PortalAppointment/PortalAppointment';
import { formatPhoneNumber, hasValue, languages, LanguageSelector } from '@amsanalytics/react-shared-library';
import { PortalValidationResult } from '../../../Models/PortalValidationResult';
import { ApplicationStatuses, CustomerRoles } from '../../../Helpers/Constants';
import { PortalLoginResult } from '../../../Models/PortalLoginResult';
import { RadioButton, RadioButtonChangeEvent } from '@progress/kendo-react-inputs';
import { useAppDispatch } from '../../../Store/useAppDispatch';
import { PortalStepItem } from '../../../Models/PortalStepItem';
import { withCurrentQuery } from '../../../Helpers/NavigationHelpers';
import { display } from '@mui/system';

// get stepper steps for the client's stepper settings. labels based on mobile or desktop view
const getSteps = (portalStepItems: PortalStepItem[], width: number, selectedLanguageId: string, hasAppointment: boolean): StepProps[] => {
  // build steps based on the portalStepItems from the server. Then sort by sortOrder.
  const steps: StepProps[] = [];
  // Add "Application" step if we should.
  if (stringToBoolean(portalStepItems.find(psi => psi.stepName.toUpperCase() === 'APPLICATION')?.show) === true) {
    steps.push({
      label: width < 600 ? getWordingForLanguage('application', selectedLanguageId) : getWordingForLanguage('appReceived', selectedLanguageId),
      svgIcon: checkIcon,
      disabled: true,
      sortOrder: portalStepItems.find(psi => psi.stepName.toUpperCase() === 'APPLICATION')?.sortOrder
    });
  }
  // Add "Documents" step if we should.
  if (stringToBoolean(portalStepItems.find(psi => psi.stepName.toUpperCase() === 'DOCUMENTS')?.show) === true) {
    steps.push({
      label: width < 600 ? getWordingForLanguage('documents', selectedLanguageId) : getWordingForLanguage('addDocuments', selectedLanguageId),
      sortOrder: portalStepItems.find(psi => psi.stepName.toUpperCase() === 'DOCUMENTS')?.sortOrder
    });
  }
  // Add "Appointment" step if we should.
  if (stringToBoolean(portalStepItems.find(psi => psi.stepName.toUpperCase() === 'APPOINTMENT')?.show) === true) {
    steps.push({
      label: width < 600 ? getWordingForLanguage('apptTitle', selectedLanguageId) : hasAppointment ? getWordingForLanguage('viewAppt', selectedLanguageId) : getWordingForLanguage('makeAppt', selectedLanguageId),
      sortOrder: portalStepItems.find(psi => psi.stepName.toUpperCase() === 'APPOINTMENT')?.sortOrder
    });
  }
  // Add "References" step if we should.
  if (stringToBoolean(portalStepItems.find(psi => psi.stepName.toUpperCase() === 'REFERENCES')?.show) === true) {
    steps.push({
      label: width < 600 ? getWordingForLanguage('references', selectedLanguageId) : getWordingForLanguage('addReferences', selectedLanguageId),
      sortOrder: portalStepItems.find(psi => psi.stepName.toUpperCase() === 'REFERENCES')?.sortOrder
    });
  }
  // sort by the sortOrder on the portalStepItems
  return steps.sort((a, b) => +a.sortOrder - +b.sortOrder);
};

interface CustomerPortalPageProps { }

export default function CustomerPortalPage(props: CustomerPortalPageProps) {
  // for dispatching actions
  const dispatch = useAppDispatch();
  // Get params from url so we can load applictionLink data.
  const params = useParams();
  const navigate = useNavigate();
  // Campaign parameters (utm_*, gclid, …) on the portal link, carried to the curated site for its chatbot.
  const { search } = useLocation();
  // Get dimensions of window so we can determine if we are on mobile or desktop.
  const { width } = useWindowDimensions();
  // whether or not we are validating the portal link. used to determine which dialog to show.
  const isValidatingPortalLink: boolean = useSelector((appState: AppState) => appState.customerPortal.isValidatingPortalLink);
  // portal link validation result
  const portalValidationResult: PortalValidationResult = useSelector((appState: AppState) => appState.customerPortal.portalValidationResult);
  // client ID
  const clientId: string | null = useSelector((appState: AppState) => appState.shared.clientId);
  // Customer portal user
  const user: CustomerPortalUser = useSelector((appState: AppState) => appState.customerPortal.user);
  // selected language ID. used to determine which language to display.
  const selectedLanguageId: string = useSelector((appState: AppState) => appState.shared.selectedLanguageId);
  // Whether or not the user can toggle between English and Spanish
  const allowSpanishTranslation = useSelector((appState: AppState) => appState.shared.allowSpanishTranslation);
  // show login dialog by default on page load.
  const [showLoginDialog, setShowLoginDialog] = useState<boolean>(true);
  // whether or not to show the "request new link" success dialog
  const [hasRequestedNewLink, setHasRequestedNewLink] = useState<boolean>(false);
  // whether or not the system is requesting a new link for the customer
  const [requestingNewLink, setRequestingNewLink] = useState<boolean>(false);
  // year value for date of birth
  const [dobYear, setDobYear] = useState<string>();
  // month ID value for date of birth
  const [dobMonthId, setDobMonthId] = useState<string>();
  // day value for date of birth
  const [dobDay, setDobDay] = useState<string>();
  // portal login info
  const [portalLoginInfo, setPortalLoginInfo] = useState<CustomerPortalLogin>({ customerPortalLinkId: (params as any)?.customerPortalLinkId });
  // whether or not user is logging in with SSN
  const [useSsnLogin, setUseSsnLogin] = useState<boolean>(true);
  // whether or not the customer has failed to login during this session. if they have, we want to show "invalid login" message.
  const [hasFailedToLogin, setHasFailedToLogin] = useState<boolean>(false);
  // whether or not the system is currently trying to login the customer
  const [loggingIn, setLoggingIn] = useState<boolean>(false);
  // stepper index. used to determine which step to display.
  const [stepperIndex, setStepperIndex] = useState<number>(1);
  // whether or not the user is scheduling an appointment
  const isSchedulingAppointment = useSelector((appState: AppState) => appState.customerPortal.isSchedulingAppointment);
  // whether or not we need additional login info from the user
  const [needsMoreLoginInfo, setNeedsMoreLoginInfo] = useState<boolean>(false);
  // selected application customer id
  const [selectedApplicationCustomerId, setSelectedApplicationCustomerId] = useState<string | null>(null);

  // check if user should be redirected to the credit application or pre-qualification page.
  const checkRedirect = async (clientId: string, customerPortalLinkId: string) => {
    const response: Response | undefined = await httpGetAsync(`CustomerPortal/checkRedirection?customerPortalLinkId=${customerPortalLinkId}`);
    if (response && response.ok) {
      // 1 -> No redirection needed
      // 2 -> Redirect to credit application page
      // 3 -> Redirect to pre-quailification page
      response.json().then((redirectResult: number) => {
        if (redirectResult === 2) {
          // redirect to to credit application page so customer can edit and accept terms.
          navigate(`/${clientId}/portal/new-credit-app/${customerPortalLinkId}`);
        } else if (redirectResult === 3) {
          // redirect to pre-qualification page
          navigate(`/${clientId}/portal/prequalify/${customerPortalLinkId}`);
        } else {
          // otherwise, get data for this portal user.
          dispatch(getUser(customerPortalLinkId));
        }
      });
    } else {
      // show error to user if an exception occurs while checking redirection.
      dispatch(portalValidationError({ httpStatusCode: 401, errorMessage: 'Unauthorized' }));
    }
  }

  // Load user data on page load.
  useEffect(() => {
    // Set page title
    document.title = getWordingForLanguage('tabTitle', selectedLanguageId);
    // Get the client Id
    const clientId = (params as any)?.clientId;
    
    if (clientId) {
      dispatch(setClient(clientId));
    }
    // set selected language ID based on user's navigator language
    dispatch(setSelectedLanguageId((navigator.language?.includes('es') || navigator.languages.some((l) => l.includes('es'))) ? '2' : '1'));
    // Get and set application link ID. Used to get user data.
    const customerPortalLinkId = (params as any)?.customerPortalLinkId;
    const step = (params as any)?.step;
    if (customerPortalLinkId) {
      dispatch(setCustomerPortalLinkId(customerPortalLinkId));
      dispatch(setUser({ ...user, customerPortalLinkId }));
      checkRedirect(clientId, customerPortalLinkId);
    }
    if(hasValue(step)) {
      setStepperIndex(parseInt(step, 10));
    }
  }, [params, dispatch]);

  // Run when the user object from redux changes.
  useEffect(() => {
    if (user) {
      if (user.applicationCustomers.length > 0) {
        // Get the applicationId from the primary applicant.
        const applicant = user.applicationCustomers.find(ac => ac.roleId.toLowerCase() === CustomerRoles.applicant);
        setSelectedApplicationCustomerId(applicant?.id ?? user.applicationCustomers[0].id);
      }
      // If customer prefers Spanish, set the language to Spanish. Otherwise, set it to English.
      dispatch(setSelectedLanguageId(user.prefersSpanish === true ? '2' : '1'));
    }
  }, [dispatch, user]);

  // Whether the customer can add documents and references. customer can add if app status is not one of the following:
  // Removed Applicant, Archived, Duplicate, Closed-Lost, Hidden, Denied, Finalized & Posted
  const canAddDocumentsAndReferences = (): boolean => {
    return isNotNil(user.applicationStatusId)
      && (user.applicationStatusId.toLowerCase() !== ApplicationStatuses.removedApplicant.toLowerCase()
        && user.applicationStatusId.toLowerCase() !== ApplicationStatuses.archived.toLowerCase()
        && user.applicationStatusId.toLowerCase() !== ApplicationStatuses.duplicate.toLowerCase()
        && user.applicationStatusId.toLowerCase() !== ApplicationStatuses.closedLost.toLowerCase()
        && user.applicationStatusId.toLowerCase() !== ApplicationStatuses.hidden.toLowerCase()
        && user.applicationStatusId.toLowerCase() !== ApplicationStatuses.denied.toLowerCase()
        && user.applicationStatusId.toLowerCase() !== ApplicationStatuses.finalizedPosted.toLowerCase());
  }

  // whether the customer can edit documents and references. customer can edit if app status is new
  const canEditDocumentsAndReferences = (): boolean => {
    return isNotNil(user.applicationStatusId) && user.applicationStatusId.toLowerCase() === ApplicationStatuses.new.toLowerCase();
  }

  // allow customer to login depending on whether they are using SSN or name+DoB.
  // if they have failed to login 5 times, disable login button. customer will need to request a new link.
  const canLogin = (): boolean => {
    return hasValue(user.customerPortalLinkId) &&
      loggingIn !== true && user.numberOfFailedLoginAttempts < 5
      && (
        (useSsnLogin === true && isNotNil(portalLoginInfo.lastFourSsn) && portalLoginInfo.lastFourSsn.length > 3) ||
        (useSsnLogin !== true && isNotNil(portalLoginInfo.lastName) && portalLoginInfo.lastName.length > 0 && hasValue(dobYear) && hasValue(dobMonthId) && hasValue(dobDay))
      );
  };

  // Call the login API to attempt to login the customer.
  const callLoginApi = async () => {
    // try logging in
    // NOTE: The customerPortalLinkId is required for the login API. If it is not present, the login will fail. DO NOT REMOVE even though it is not directly
    // used in the controller.
    const dateOfBirth =  `${dobYear}-${dobMonthId?.padStart(2, '0')}-${dobDay?.padStart(2, '0')}`;
    const successfulLogin: Response | undefined = await httpPostAsync(`CustomerPortal/login?customerPortalLinkId=${user.customerPortalLinkId}`,
      {
        ...portalLoginInfo,
        dateOfBirth: hasValue(dobMonthId) ? new Date(dateOfBirth).toISOString() : undefined,
        applicationCustomerId: selectedApplicationCustomerId
      });
    return successfulLogin;
  }

  // attempt to login
  const attemptLogin = async () => {
    setLoggingIn(true);
    await callLoginApi().then(response => {
      if (response && response.ok) {
        response.json().then((loginResult: number) => {
          if (loginResult === PortalLoginResult.Success) {
            // clear login info and hide login dialog on successful login.
            setPortalLoginInfo({ customerPortalLinkId: user?.customerPortalLinkId });
            setShowLoginDialog(false);
          } else if (loginResult === PortalLoginResult.ViewCuratedVehicles) { // Was successful and to redirect to curated vehicles page
            navigate(withCurrentQuery(`/${clientId}/curated/${user.customerPortalLinkId}`, search));
          } else if (loginResult === PortalLoginResult.AcknowledgementNeeded || loginResult === PortalLoginResult.AddCustomerToApplication) {
            // Get customerId from the applicationCustomer
            const customerId = user.applicationCustomers.find(ac => ac.id === selectedApplicationCustomerId)?.customerId;
            navigate(`/${clientId}/portal/credit-app/${user.customerPortalLinkId}/${customerId!}`);
          } else {
            // if login fails, increment failed login and show login failure message.
            setHasFailedToLogin(true);
            dispatch(failedLogin());
          }
        });
      } else {
        // display error message if an exception occurs while logging in.
        dispatch(displayError(getWordingForLanguage('loginErrorMessage', selectedLanguageId)));
      }
    }).finally(() => {
      setLoggingIn(false);
    });
  };

  // user is requesting a new application link. show loading indicator while we wait for response.
  // after a successful response, notify the user that the store will contact them soon with a new link.
  const callRequestNewApplicationLinkApi = async () => {
    const successfulRequest: Response | undefined = await httpGetAsync(`CustomerPortal/newLink?customerPortalLinkId=${user.customerPortalLinkId}`);
    return successfulRequest;
  }

  const requestNewApplicationLink = async () => {
    setRequestingNewLink(true);
    await callRequestNewApplicationLinkApi().then(response => {
      if (response && response.ok) {
        response.json().then((successfulRequest: boolean) => {
          if (successfulRequest && successfulRequest === true) {
            setHasRequestedNewLink(true);
          } else {
            dispatch(displayError(getWordingForLanguage('requestLinkErrorMessage', selectedLanguageId)));
          }
        });
      } else {
        dispatch(displayError(getWordingForLanguage('requestLinkErrorMessage', selectedLanguageId)));
      }
    }).finally(() => {
      setRequestingNewLink(false);
      setHasRequestedNewLink(true);
    });
  }

  // returns true if the portal link is invalid. In this cas, we want to allow the user to request a new link.
  const isInvalidPortalLink = (): boolean => {
    return (isNotNil(portalValidationResult) && isNotNil(portalValidationResult.httpStatusCode) && portalValidationResult.httpStatusCode !== 200)
      || (hasFailedToLogin === true && user.numberOfFailedLoginAttempts >= 5);
  }

  // returns the correct dialog title text, depending on whether the user is requesting a new link or logging in.
  const getDialogTitle = (): string => {
    return isValidatingPortalLink === true ? getWordingForLanguage('validatingLinkTitle', selectedLanguageId)
      : (isInvalidPortalLink() === true && hasRequestedNewLink !== true)
        ? getWordingForLanguage('invalidLinkTitle', selectedLanguageId)
        : (isInvalidPortalLink() === true && hasRequestedNewLink === true)
          ? getWordingForLanguage('requestLinkSuccessTitle', selectedLanguageId)
          : getWordingForLanguage('signInTitle', selectedLanguageId);
  }

  // returns the correct dialog body text, depending on whether the user is requesting a new link or logging in.
  const getDialogBody = (): string => {
    return isValidatingPortalLink === true ? getWordingForLanguage('validatingLinkMessage', selectedLanguageId)
      : loggingIn === true ? getWordingForLanguage('loggingIn', selectedLanguageId)
        : needsMoreLoginInfo ? getWordingForLanguage('needMoreInfo', selectedLanguageId)
          : (isInvalidPortalLink() === true && hasRequestedNewLink !== true)
            ? getWordingForLanguage('invalidOrExpiredLink', selectedLanguageId)
            : (isInvalidPortalLink() === true && hasRequestedNewLink === true)
              ? `${getWordingForLanguage('requestLinkSuccessMessage', selectedLanguageId)} ${getWordingForLanguage('questionsCallUs', selectedLanguageId)} ${formatPhoneNumber(user.assignedEmployeeStorePhone)}`
              : useSsnLogin === true
                ? getWordingForLanguage('lastFourDescription', selectedLanguageId)
                : getWordingForLanguage('enterNameBirthday', selectedLanguageId);
  }

  const showIncorrectLoginMessage = (): boolean => {
    return hasFailedToLogin === true && user.numberOfFailedLoginAttempts < 5;
  }
  const customerName = selectedApplicationCustomerId ? user.applicationCustomers.find(c => c.id === selectedApplicationCustomerId)!.customerName ?? '' : '';
  return isValidatingPortalLink === true ? (
    <div className={styles.flexColumnFullCenter}>
      <Dialog title={getDialogTitle()} closeIcon={false}>
        <p style={{ marginTop: 0 }}>{getDialogBody()}</p>
        <Loader type="pulsing" themeColor='primary' style={{ display: 'flex', justifyContent: 'center' }} />
      </Dialog>
    </div>
  )
    : showLoginDialog ? (
      <div className={styles.flexColumnFull}>
        <Dialog title={getDialogTitle()} closeIcon={false}>
          {hasRequestedNewLink !== true && <p style={{ margin: 0, maxWidth: '300px', textAlign: 'center' }}><b>{getWordingForLanguage('signInToContinue', selectedLanguageId)}</b></p>}
          <p style={(needsMoreLoginInfo && loggingIn !== true) ? { color: '#8B0000', fontWeight: 500, maxWidth: '300px', marginTop: 0, textAlign: 'center' } : { maxWidth: '300px', marginTop: 0, textAlign: 'center' }}>{getDialogBody()}</p>
          {isInvalidPortalLink() !== true && user.applicationCustomers.length > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', paddingBottom: '5px' }}>
              {user.applicationCustomers.sort(sortCustomers).map((applicationCustomer, index) => (
                <RadioButton
                  key={index}
                  style={index === 1 ? { marginLeft: '10px' } : {}}
                  checked={selectedApplicationCustomerId === applicationCustomer.id}
                  name="applicationCustomerId"
                  value={applicationCustomer.id}
                  label={getCustomerRoleName(applicationCustomer.roleId, selectedLanguageId)}
                  onChange={(e: RadioButtonChangeEvent) => {
                    setSelectedApplicationCustomerId(e.value);
                  }}
                />
              ))}
            </div>
          )}
          {selectedApplicationCustomerId && isInvalidPortalLink() !== true && !(isNil(user.customerPortalLinkId) || !hasValue(user.customerPortalLinkId)) && useSsnLogin === true && (
            <SSNInputGroup
              canLogin={canLogin()}
              loggingIn={loggingIn}
              selectedLanguageId={selectedLanguageId}
              ssnLastFour={portalLoginInfo.lastFourSsn}
              onChange={(value: string) => setPortalLoginInfo({ ...portalLoginInfo, lastFourSsn: value })}
              onUseAlternateLogin={() => setUseSsnLogin(false)}
              onLogin={() => {
                // if last four of SSN are all the same, we need more info.
                if (portalLoginInfo.lastFourSsn && portalLoginInfo.lastFourSsn.length === 4 && new Set(portalLoginInfo.lastFourSsn).size === 1) {
                  // customer must login using name and date of birth, so show name+DoB login and disable SSN login.
                  setNeedsMoreLoginInfo(true);
                  setPortalLoginInfo({ customerPortalLinkId: user?.customerPortalLinkId, lastName: '' });
                  setUseSsnLogin(false);
                } else {
                  // otherwise, attempt to login.
                  attemptLogin();
                }
              }}
            />
          )}
          {selectedApplicationCustomerId && isInvalidPortalLink() !== true && !(isNil(user.customerPortalLinkId) || !hasValue(user.customerPortalLinkId)) && useSsnLogin !== true && (
            <NameDoBLogin
              canLogin={canLogin()}
              loggingIn={loggingIn}
              lastName={portalLoginInfo.lastName}
              year={dobYear}
              month={dobMonthId}
              day={dobDay}
              selectedLanguageId={selectedLanguageId}
              onLastNameChange={(value: string) => setPortalLoginInfo({ ...portalLoginInfo, lastName: value })}
              onYearChange={(value: string) => {
                setDobYear(value);
              }}
              onMonthChange={(monthId: string) => {
                setDobMonthId(monthId);
              }}
              onDayChange={(value: string) => setDobDay(value)}
              onUseAlternateLogin={() => setUseSsnLogin(true)}
              onLogin={() => attemptLogin()}
            />
          )}
          {showIncorrectLoginMessage() && (
            <p style={{ color: '#8B0000', fontWeight: 500, margin: '0 0 1em 0', textAlign: 'center' }}>{getWordingForLanguage('incorrectLoginMessage', selectedLanguageId)}</p>
          )}
          {allowSpanishTranslation && (
            <div style={{ display: 'flex', justifyContent: 'center' }} >
              <LanguageSelector
                selectedLanguageId={selectedLanguageId}
                languages={languages}
                style={{ margin: '0 0 .5em 0', width: 'fit-content' }}
                label={selectedLanguageId === '1' ? 'Preferred Language' : 'Idioma Preferido'}
                onSelectLanguage={(languageId: string) => {
                  dispatch(setSelectedLanguageId(languageId));
                  // update browser tab title based on the selected language
                  document.title = getWordingForLanguage('tabTitle', selectedLanguageId);
                  // Update the "PrefersSpanish" flag on the contact record
                  isNotNil(user.contactId) && dispatch(updatePrefersSpanish(user.contactId, user.customerPortalLinkId, languageId === '2'));
                }}
              />
            </div>
          )}
          {isInvalidPortalLink() === true && hasRequestedNewLink !== true && (
            <Button
              themeColor={'primary'}
              style={{ marginTop: '0', width: '100%' }}
              onClick={() => requestNewApplicationLink()}
              disabled={requestingNewLink === true}
            >
              {requestingNewLink === true ? <Loader type="pulsing" style={{ marginRight: '3px' }} themeColor='dark' /> : <SvgIcon icon={linkVerticalIcon} size="small" style={{ marginRight: '3px' }} />}
              {requestingNewLink === true ? getWordingForLanguage('requestingNewLink', selectedLanguageId) : getWordingForLanguage('requestLink', selectedLanguageId)}
            </Button>
          )}

        </Dialog>
      </div>
    ) : (
      <div className={styles.customerPortalContainer}>
        {allowSpanishTranslation && (
          <LanguageSelector
            selectedLanguageId={selectedLanguageId}
            languages={languages}
            style={{ margin: '0 0 .5em 0', width: 'fit-content' }}
            label={selectedLanguageId === '1' ? 'Preferred Language' : 'Idioma Preferido'}
            onSelectLanguage={(languageId: string) => {
              dispatch(setSelectedLanguageId(languageId));
              // update browser tab title based on the selected language
              document.title = getWordingForLanguage('tabTitle', selectedLanguageId);
              // Update the "PrefersSpanish" flag on the contact record
              isNotNil(user.contactId) && dispatch(updatePrefersSpanish(user.contactId, user.customerPortalLinkId, languageId === '2'));
            }}
          />
        )}
        <h2 style={{ margin: '10px 0' }}>{capitalizeFirstLetterOfEachWord(customerName)}</h2>
        <p style={{ margin: '0 0 2em 0' }}>{getWordingForLanguage('thanks', selectedLanguageId)}{user.assignedEmployeeStoreName}&nbsp;{getWordingForLanguage('thanksContinued', selectedLanguageId)}</p>
        {
          width < 600 && (
            <>
              <Stepper
                value={stepperIndex}
                onChange={(e: StepperChangeEvent) => setStepperIndex(e.value)}
                items={getSteps(user.portalStepItems, width, selectedLanguageId, isNotNil(user.appointmentDate))}
                orientation={'horizontal'}
                style={{ marginBottom: ' 2em', height: 'fit-content', width: '100%' }
                }
              />
              {stepperIndex === 1 && (<PortalStipulations disptach={dispatch} canAddDocs={canAddDocumentsAndReferences()} canEditDocs={canEditDocumentsAndReferences()} clientId={clientId} selectedLanguageId={selectedLanguageId} user={user} selectedApplicationCustomerId={selectedApplicationCustomerId!} width={width} />)}
              {stepperIndex === 2 && (<PortalAppointment disptach={dispatch} isSchedulingAppointment={isSchedulingAppointment} selectedLanguageId={selectedLanguageId} user={user} selectedApplicationCustomerId={selectedApplicationCustomerId!} onScheduleAppointment={(date: Date) => dispatch(scheduleAppointment(user.customerPortalLinkId, date))} />)}
              {stepperIndex === 3 && (<PortalReferences dispatch={dispatch} canAddReferences={canAddDocumentsAndReferences()} canEditReferences={canEditDocumentsAndReferences()} selectedLanguageId={selectedLanguageId} width={width} user={user} selectedApplicationCustomerId={selectedApplicationCustomerId!} />)}
            </>
          )
        }
        {
          width >= 600 && (
            <div className={styles.desktopContainer}>
              <Stepper
                value={stepperIndex}
                onChange={(e: StepperChangeEvent) => setStepperIndex(e.value)}
                items={getSteps(user.portalStepItems, width, selectedLanguageId, isNotNil(user.appointmentDate))}
                orientation={'horizontal'}
                style={{ height: 'fit-content', width: '100%', maxWidth: '800px' }}
              />
              <div>
                {stepperIndex === 1 && (<PortalStipulations disptach={dispatch} canAddDocs={canAddDocumentsAndReferences()} canEditDocs={canEditDocumentsAndReferences()} clientId={clientId} selectedLanguageId={selectedLanguageId} user={user} selectedApplicationCustomerId={selectedApplicationCustomerId!} width={width} />)}
                {stepperIndex === 2 && (<PortalAppointment disptach={dispatch} isSchedulingAppointment={isSchedulingAppointment} selectedLanguageId={selectedLanguageId} user={user} selectedApplicationCustomerId={selectedApplicationCustomerId!} onScheduleAppointment={(date: Date) => dispatch(scheduleAppointment(user.customerPortalLinkId, date))} />)}
                {stepperIndex === 3 && (<PortalReferences dispatch={dispatch} canAddReferences={canAddDocumentsAndReferences()} canEditReferences={canEditDocumentsAndReferences()} selectedLanguageId={selectedLanguageId} width={width} user={user} selectedApplicationCustomerId={selectedApplicationCustomerId!} />)}
              </div>
            </div>
          )
        }
      </div>
    );
}
